"use client";

import { AlertCircle } from "lucide-react";
import type { MediaUploadStatus } from "@/hooks/use-media-upload";
import { formatDuration, formatFileSize } from "./media-utils";

export interface MediaUploadState {
  status?: MediaUploadStatus;
  progress?: number;
  error?: string | null;
  uploadedBytes?: number;
  uploadSpeed?: number;
  eta?: number;
  sizeBytes?: number;
}

export const isUploadBusy = ({ status }: MediaUploadState) =>
  !!status && status !== "done" && status !== "error";

const Spinner = ({ label }: { label: string }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/50">
    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
    <p className="text-[11px] text-white/80">{label}</p>
  </div>
);

/** Covers the thumbnail of whatever is rendering an in-flight upload. */
export const MediaUploadOverlay = ({
  status,
  progress,
  error,
  uploadedBytes,
  uploadSpeed,
  eta,
  sizeBytes,
}: MediaUploadState) => {
  if (status === "error" || error) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-destructive/10 px-2">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <p className="text-center text-[11px] leading-tight text-destructive">
          {error ?? "Upload failed"}
        </p>
      </div>
    );
  }

  if (status === "creating") return <Spinner label="Preparing…" />;
  if (status === "processing") return <Spinner label="Processing…" />;
  if (status !== "uploading") return null;

  // Progress only becomes meaningful once the first chunk is acknowledged.
  if (!progress) return <Spinner label="Uploading…" />;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/50 px-3">
      <div className="h-1.5 w-3/4 overflow-hidden rounded-full bg-white/25">
        <div
          className="h-full rounded-full bg-white transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
      {uploadedBytes != null && uploadSpeed != null && (
        <p className="text-center text-[11px] leading-tight text-white/80">
          {formatFileSize(uploadedBytes)} / {formatFileSize(sizeBytes)} · ~{formatDuration(eta)}
        </p>
      )}
    </div>
  );
};
